import { createContext, useCallback, useContext, useEffect, useMemo } from 'react'
import type { ReactNode } from 'react'
import { useTenant } from '@/tenant/TenantProvider'
import { useDashboardStore } from '@/state/useDashboardStore'
import { connectLiveEvents } from '@/data/liveEvents'
import { useTrophyStore } from '@/state/useTrophyStore'
import { createDataService } from './index'
import type { DataService } from './types'
import { TIER_MAPPINGS } from '@/milestones/evolution'
import type { Category } from '@/milestones/types'
import { DEFAULT_MILESTONES } from '@/milestones/definitions'
import { isMilestoneReached } from '@/milestones/detector'
import { buildInitialSnapshot, buildZeroStateSnapshot } from './mock/generators'
import { makeRng } from './mock/seed'

interface DataContextValue {
  service: DataService
  
  bumpCategoryValue(category: Category, delta: number): void
  
  loadMockSnapshot(): void
  
  clearSnapshot(): void
}

const DataContext = createContext<DataContextValue | null>(null)

export function DataPipe({ children }: { children: ReactNode }) {
  const tenant = useTenant()
  const service = useMemo(() => createDataService(tenant), [tenant])
  const setSnapshot = useDashboardStore((s) => s.setSnapshot)
  const pushLiveActivity = useDashboardStore((s) => s.pushLiveActivity)
  const pushEvent = useDashboardStore((s) => s.pushEvent)
  const bump = useDashboardStore((s) => s.bump)
  const initTrophies = useTrophyStore((s) => s.initFor)

  useEffect(() => {
    initTrophies(tenant.id)
  }, [tenant.id, initTrophies])

  useEffect(() => {
    setSnapshot(service.getSnapshot())
    const unsubscribe = service.subscribe((event) => {
      pushEvent(event)
      bump()
    })
    const unsubscribeSnapshot = service.onSnapshot?.((snap) => {
      setSnapshot(snap)
      bump()
    })
    service.start()

    // Živý proud běží vedle dávkové cesty. Jen předbíhá feed o pár desítek
    // sekund — všechna čísla dál pocházejí ze snapshotu.
    const sseUrl = import.meta.env.VITE_SSE_URL as string | undefined
    const sseKey = import.meta.env.VITE_SSE_KEY as string | undefined
    const live =
      sseUrl && sseKey
        ? connectLiveEvents(sseUrl, sseKey, (e) => {
            const id = String(e.event_id ?? '')
            if (!id) return
            const name = String(e.event_name ?? '')
            // Snapshot ve feedu ukazuje jen usage eventy. Živý proud vede
            // i admin kanál s technickým šumem (presence.heartbeat apod.),
            // takže ho zarovnáme na stejný výběr — jinak by se obě cesty
            // rozcházely a feed by zaplavily hearbeaty.
            if (!name.startsWith('usage.')) return
            const venueId = String(e.venue_id ?? '')
            const snap = useDashboardStore.getState().snapshot
            // Živý proud jde z Redisu rovnou, takže obchází deny list, který
            // se ve skladu uplatňuje joinem na v_analytics_eligible_venue.
            // Adresář poboček ten filtr už nese, takže co v něm není, sem
            // nepatří — testovací pobočky ani události bez pobočky.
            // Dokud snapshot nedorazí, radši nic nepouštíme; dávková cesta
            // to stejně do minuty doplní.
            if (!snap || !venueId || !snap.venueDirectory[venueId]) return
            pushLiveActivity({
              id,
              eventName: name,
              venueId,
              venueName: snap.venueDirectory[venueId],
              status: String(e.dims?.status ?? ''),
              integration: String(e.dims?.integration ?? ''),
              at: String(e.occurred_at ?? new Date().toISOString()),
            })
          })
        : null

    return () => {
      unsubscribe()
      unsubscribeSnapshot?.()
      live?.stop()
      service.stop()
    }
  }, [service, setSnapshot, pushEvent, pushLiveActivity, bump])

  const bumpCategoryValue = useCallback(
    (category: Category, delta: number) => {
      const cur = useDashboardStore.getState().snapshot
      if (!cur) return
      const m = TIER_MAPPINGS[category]
      let next = cur

      switch (m.metric) {
        case 'clients.active':
          next = {
            ...cur,
            clients: { ...cur.clients, active: Math.max(0, cur.clients.active + delta) },
          }
          break
        case 'revenue.mrrCzk': {
          const newMrr = Math.max(0, (cur.revenue.mrrCzk ?? 0) + delta)
          const arpu = cur.clients.active > 0 ? Math.round(newMrr / cur.clients.active) : 0
          next = {
            ...cur,
            revenue: {
              ...cur.revenue,
              mrrCzk: newMrr,
              arpuCzk: arpu,
              cashflowMonthCzk: newMrr - (cur.costs.fixedMonthlyCzk ?? 0),
            },
          }
          break
        }
        case 'reliability.daysWithoutP1':
          next = {
            ...cur,
            incidents: {
              ...cur.incidents,
              daysWithoutP1: Math.max(0, cur.incidents.daysWithoutP1 + delta),
            },
          }
          break
        case 'incidents.closedTotal':
          next = {
            ...cur,
            incidents: {
              ...cur.incidents,
              closedTotal: Math.max(0, cur.incidents.closedTotal + delta),
              closedToday: Math.max(0, cur.incidents.closedToday + Math.max(0, delta)),
            },
          }
          break
        default:
          break
      }

      next = { ...next, asOf: new Date().toISOString() }
      setSnapshot(next)
      bump()
    },
    [setSnapshot, bump],
  )

  const loadMockSnapshot = useCallback(() => {
    const rng = makeRng(`${tenant.id}-mock-${Date.now()}`)
    const scale = { locations: 312, mrrBaselineCzk: 1_500_000, fixedMonthlyCzk: 820_000 }
    const snap = buildInitialSnapshot(rng, scale)
    const ids = DEFAULT_MILESTONES.filter((d) => isMilestoneReached(d, snap)).map((d) => d.id)
    if (ids.length > 0) useTrophyStore.getState().unlockMany(ids)
    setSnapshot(snap)
    bump()
  }, [tenant.id, setSnapshot, bump])

  const clearSnapshot = useCallback(() => {
    const snap = buildZeroStateSnapshot()
    useTrophyStore.getState().reset()
    setSnapshot(snap)
    bump()
  }, [setSnapshot, bump])

  const value = useMemo<DataContextValue>(
    () => ({ service, bumpCategoryValue, loadMockSnapshot, clearSnapshot }),
    [service, bumpCategoryValue, loadMockSnapshot, clearSnapshot],
  )

  return <DataContext.Provider value={value}>{children}</DataContext.Provider>
}

export function useDataService(): DataService {
  const ctx = useContext(DataContext)
  if (!ctx) throw new Error('useDataService must be used within <DataPipe>')
  return ctx.service
}

export function useBumpCategoryValue(): DataContextValue['bumpCategoryValue'] {
  const ctx = useContext(DataContext)
  if (!ctx) throw new Error('useBumpCategoryValue must be used within <DataPipe>')
  return ctx.bumpCategoryValue
}

export function useLoadMockSnapshot(): DataContextValue['loadMockSnapshot'] {
  const ctx = useContext(DataContext)
  if (!ctx) throw new Error('useLoadMockSnapshot must be used within <DataPipe>')
  return ctx.loadMockSnapshot
}

export function useClearSnapshot(): DataContextValue['clearSnapshot'] {
  const ctx = useContext(DataContext)
  if (!ctx) throw new Error('useClearSnapshot must be used within <DataPipe>')
  return ctx.clearSnapshot
}
